import { defineStore } from 'pinia'
import _ from 'lodash'
import { State } from 'country-state-city'

import { resourceApi } from './common/api'
import {
  getAllCountries,
  rules,
  defaultItemsPerPage
} from './common/commonFunctions'

const institutionApi = resourceApi.extend('institution')

export const useInstitution = defineStore('institution', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['itemsPerPage'],
      storage: localStorage
    }
  ],

  state() {
    return {
      rules: rules.institution,
      isLoadingActiveInstitutions: false,
      errorActiveInstitutions: null,
      statusActiveInstitutions: null,
      isLoadingArchivedInstitutions: false,
      errorArchivedInstitutions: null,
      statusArchivedInstitutions: null,
      isLoadingArchivingInstitutions: false,
      errorArchivingInstitutions: null,
      statusArchivingInstitutions: null,
      isLoadingRestoringInstitutions: false,
      errorRestoringInstitutions: null,
      statusRestoringInstitutions: null,
      isLoadingEditingInstitution: false,
      errorEditingInstitution: null,
      statusEditingInstitution: null,
      isLoadingCreatingInstitution: false,
      errorCreatingInstitution: null,
      statusCreatingInstitution: null,
      isActionMultiple: null,
      activeInstitutions: null,
      archivedInstitutions: null,
      fetchInstitutionsFrequency: 300000, // 5 minutes
      fetchActiveInstitutionsInterval: null,
      fetchArchivedInstitutionsInterval: null,
      adminTabIndex: null,
      adminTableData: null,
      editInstitution: null,
      selectedInstitutionIds: null,
      itemsPerPage: {
        active: defaultItemsPerPage,
        archived: defaultItemsPerPage
      }
    }
  },

  getters: {
    getActiveInstitutions: ({ activeInstitutions }) => {
      return activeInstitutions
    },
    getDirectAccessInstitutions: ({ activeInstitutions }) => {
      return (activeInstitutions || []).filter(
        (institution) =>
          institution?.directAccessEnabled === true ||
          institution?.directAccessEnabled === 1 ||
          institution?.directAccessEnabled === '1'
      )
    },
    getAdminTabs: ({
      activeInstitutions,
      archivedInstitutions,
      itemsPerPage
    }) => {
      // Keep the list to identity + status columns so the page never needs
      // horizontal scroll. Website / HubSpot stay on the edit sheet.
      const defaultHeaders = [
        {
          title: 'Institution',
          key: 'name',
          value: 'name',
          width: '36%'
        },
        {
          title: 'Location',
          key: 'location',
          value: 'location',
          width: '24%'
        },
        {
          title: 'Login',
          key: 'directAccessEnabled',
          value: 'directAccessEnabled',
          align: 'start',
          width: '12%'
        },
        {
          title: 'Licenses',
          key: 'institutionLicense',
          value: 'institutionLicense',
          align: 'start',
          width: '16%'
        }
      ]
      const filterKeys = [
        'name',
        'location',
        'url',
        'hubspotUrl',
        'directAccessEnabled'
      ]
      const customFilters = {
        location: (value, search) => {
          if (!search) return true
          return String(value || '')
            .toLowerCase()
            .includes(String(search).toLowerCase())
        },
        directAccessEnabled: (value, search) => {
          if (!search) return true
          const needle = String(search).toLowerCase()
          const label = value ? 'direct access' : 'standard'
          return label.includes(needle)
        }
      }
      const customSort = {
        location: (left, right) =>
          String(left || '').localeCompare(String(right || '')),
        institutionLicense: (left, right) => {
          const total = (licenses) =>
            (Array.isArray(licenses) ? licenses : []).reduce(
              (sum, row) => sum + Number(row?.totalDevices || 0),
              0
            )
          return total(left) - total(right)
        }
      }
      const mapInstitutionRow = (institution) => {
        const country = {
          name: getAllCountries?.[institution?.countryCode]?.name,
          code: institution?.countryCode
        }
        const stateName =
          State.getStateByCodeAndCountry(
            institution?.state,
            institution?.countryCode
          )?.name || null
        const state = { code: institution?.state, name: stateName }
        const location = [country.name, state.name].filter(Boolean).join(', ')
        return {
          ..._.omit(institution, ['countryCode', 'state']),
          country,
          _state: state,
          location
        }
      }
      const tabs = [
        {
          text: 'Active',
          value: 'active',
          itemsPerPage: itemsPerPage.active,
          count: activeInstitutions?.length || null,
          headers: [
            ...defaultHeaders,
            {
              key: 'actions-active',
              sortable: false,
              width: '56px'
            }
          ],
          data: activeInstitutions?.length
            ? _.map(activeInstitutions, mapInstitutionRow)
            : [],
          filterKeys,
          customFilters,
          customSort
        },
        {
          text: 'Archived',
          value: 'archived',
          itemsPerPage: itemsPerPage.archived,
          count: archivedInstitutions?.length || null,
          headers: [
            ...defaultHeaders,
            {
              key: 'actions-archived',
              sortable: false,
              width: '56px'
            }
          ],
          data: archivedInstitutions?.length
            ? _.map(archivedInstitutions, mapInstitutionRow)
            : [],
          filterKeys,
          customFilters,
          customSort
        }
      ]
      return _.map(
        _.filter(tabs, (t, i) => i == 0 || t.count > 0),
        (t, i) => {
          t.index = i
          return t
        }
      )
    },
    getEditInstitution: ({ editInstitution }) => {
      return editInstitution
    },
    getAdminTableData: ({ adminTableData }) => {
      return adminTableData
    },
    getFetchInstitutionsFrequency: ({ fetchInstitutionsFrequency }) => {
      return fetchInstitutionsFrequency
    },
    getFetchActiveInstitutionsInterval: ({
      fetchActiveInstitutionsInterval
    }) => {
      return fetchActiveInstitutionsInterval
    },
    getFetchArchivedInstitutionsInterval: ({
      fetchArchivedInstitutionsInterval
    }) => {
      return fetchArchivedInstitutionsInterval
    },
    getIsLoadingActiveInstitutions: ({ isLoadingActiveInstitutions }) => {
      return isLoadingActiveInstitutions
    },
    getIsLoadingArchivedInstitutions: ({ isLoadingArchivedInstitutions }) => {
      return isLoadingArchivedInstitutions
    },
    getIsLoadingArchivingInstitutions: ({ isLoadingArchivingInstitution }) => {
      return isLoadingArchivingInstitution
    },
    getIsLoadingRestoringInstitutions: ({ isLoadingRestoringInstitution }) => {
      return isLoadingRestoringInstitution
    },
    getIsLoadingEditingInstitution: ({ isLoadingEditingInstitution }) => {
      return isLoadingEditingInstitution
    },
    getIsLoadingCreatingInstitution: ({ isLoadingCreatingInstitution }) => {
      return isLoadingCreatingInstitution
    },
    getIsLoadingMessage: ({
      isLoadingArchivingInstitutions,
      isLoadingRestoringInstitutions,
      isLoadingEditingInstitution,
      isLoadingCreatingInstitution,
      isActionMultiple
    }) => {
      if (isLoadingArchivingInstitutions || isLoadingRestoringInstitutions)
        return isActionMultiple
          ? `${isLoadingArchivingInstitutions ? 'Archiving' : 'Restoring'} institution...`
          : `${isLoadingArchivingInstitutions ? 'Archiving' : 'Restoring'} institution...`
      else if (isLoadingEditingInstitution) return 'Updating institution...'
      else if (isLoadingCreatingInstitution) return 'Creating institution...'
      else return null
    },
    getErrors: ({
      errorActiveInstitutions,
      errorArchivedInstitutions,
      errorArchivingInstitutions,
      errorRestoringInstitutions,
      errorEditingInstitution,
      errorCreatingInstitution
    }) => {
      let returnValue = []
      if (errorActiveInstitutions)
        returnValue.push({
          text: errorActiveInstitutions,
          key: 'activeInstitutions'
        })
      if (errorArchivedInstitutions)
        returnValue.push({
          text: errorArchivedInstitutions,
          key: 'archivedInstitutions'
        })
      if (errorArchivingInstitutions)
        returnValue.push({
          text: errorArchivingInstitutions,
          key: 'archivingInstitutions'
        })
      if (errorRestoringInstitutions)
        returnValue.push({
          text: errorRestoringInstitutions,
          key: 'restoringInstitutions'
        })
      if (errorEditingInstitution)
        returnValue.push({
          text: errorEditingInstitution,
          key: 'editingInstitution'
        })
      if (errorCreatingInstitution)
        returnValue.push({
          text: errorCreatingInstitution,
          key: 'creatingInstitution'
        })
      return returnValue
    }
  },

  actions: {
    async fetchActiveInstitutions(withLoader = true) {
      if (this.errorActiveInstitutions != null)
        this.errorActiveInstitutions = null
      this.statusActiveInstitutions = null
      if (withLoader) this.isLoadingActiveInstitutions = true
      try {
        const result = await institutionApi.get('', { params: { active: 1 } })
        const { error, institution } = result.data.response
        this.statusActiveInstitutions = result.data.status
        if (error) this.errorActiveInstitutions = error.message
        else this.activeInstitutions = institution
      } catch (e) {
        this.errorActiveInstitutions =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingActiveInstitutions = false
    },
    async fetchArchivedInstitutions(withLoader = true) {
      if (this.errorArchivedInstitutions != null)
        this.errorArchivedInstitutions = null
      this.statusArchivedInstitutions = null
      if (withLoader) this.isLoadingArchivedInstitutions = true
      try {
        const result = await institutionApi.get('', { params: { active: 0 } })
        const { error, institution } = result.data.response
        this.statusArchivedInstitutions = result.data.status
        if (error) this.errorArchivedInstitutions = error.message
        else this.archivedInstitutions = institution
      } catch (e) {
        this.errorArchivedInstitutions =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivedInstitutions = false
    },
    async archivingInstitutions(institutions) {
      this.isActionMultiple = institutions?.length > 1
      this.errorArchivingInstitutions = null
      this.isLoadingArchivingInstitutions = true
      this.statusArchivingInstitutions = null
      const payload = {
        where: {
          id: {
            in: _.map(institutions, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'softDelete'
        }
      }
      try {
        const result = await institutionApi.patch('', payload)
        const { error, institution } = result.data.response
        this.statusArchivingInstitutions = result.data.status
        if (error) this.errorArchivingInstitutions = error.message
        else {
          this.archivedInstitutions = [
            ...(this.archivedInstitutions ?? []),
            ...institution
          ]
          if (
            this.activeInstitutions != null &&
            this.activeInstitutions.length
          ) {
            const archivedInstitutionIds = _.map(institution, (i) => i.id)
            _.remove(this.activeInstitutions, (i) =>
              archivedInstitutionIds.includes(i.id)
            )
          }
        }
      } catch (e) {
        this.errorArchivingInstitutions =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivingInstitutions = false
      this.isActionMultiple = null
    },
    async restoringInstitutions(institutions) {
      this.isActionMultiple = institutions?.length > 1
      this.errorRestoringInstitutions = null
      this.isLoadingRestoringInstitutions = true
      this.statusRestoringInstitutions = null
      const payload = {
        where: {
          id: {
            in: _.map(institutions, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'restore'
        }
      }
      try {
        const result = await institutionApi.patch('', payload)
        const { error, institution } = result.data.response
        this.statusRestoringInstitutions = result.data.status
        if (error) this.errorRestoringInstitutions = error.message
        else {
          this.activeInstitutions = [
            ...(this.activeInstitutions ?? []),
            ...institution
          ]
          if (
            this.archivedInstitutions != null &&
            this.archivedInstitutions.length
          ) {
            const activeInstitutionIds = _.map(institution, (i) => i.id)
            _.remove(this.archivedInstitutions, (i) =>
              activeInstitutionIds.includes(i.id)
            )
          }
        }
      } catch (e) {
        this.errorRestoringInstitutions =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingRestoringInstitutions = false
      this.isActionMultiple = null
    },
    async editingInstitution(institution) {
      let {
        id,
        name,
        country,
        state,
        url: institutionUrl,
        hubspotUrl,
        directAccessEnabled,
        scenarioShareLinksEnabled
      } = institution
      if (name != null) name = name.replace(/^\s+|\s+$/g, '')
      if (institutionUrl != null) institutionUrl = institutionUrl.replace(/^\s+|\s+$/g, '')
      if (hubspotUrl != null) hubspotUrl = hubspotUrl.replace(/^\s+|\s+$/g, '')
      const wasDirectAccess = !!(this.editInstitution?.directAccessEnabled === true || this.editInstitution?.directAccessEnabled === 1)
      const nextDirectAccess = !!directAccessEnabled
      const wasShareLinks = !!(
        this.editInstitution?.scenarioShareLinksEnabled === true ||
        this.editInstitution?.scenarioShareLinksEnabled === 1
      )
      const nextShareLinks = !!scenarioShareLinksEnabled
      if (
        name != this.editInstitution?.name ||
        country?.code != this.editInstitution?.country?.code ||
        state?.code != this.editInstitution?._state?.code ||
        institutionUrl != this.editInstitution?.url ||
        hubspotUrl != this.editInstitution?.hubspotUrl ||
        nextDirectAccess != wasDirectAccess ||
        nextShareLinks != wasShareLinks
      ) {
        this.errorEditingInstitution = null
        this.isLoadingEditingInstitution = true
        this.statusEditingInstitution = null
        let payload = {
          where: { id },
          data: {}
        }
        if (name != this.editInstitution?.name) payload.data.name = name
        if (country?.code != this.editInstitution?.country?.code)
          payload.data.countryCode = country?.code
        if (state?.code != this.editInstitution?._state?.code)
          payload.data.state = state?.code
        if (institutionUrl != this.editInstitution?.url) payload.data.url = institutionUrl
        if (hubspotUrl != this.editInstitution?.hubspotUrl)
          payload.data.hubspotUrl = hubspotUrl
        if (nextDirectAccess != wasDirectAccess)
          payload.data.directAccessEnabled = nextDirectAccess
        if (nextShareLinks != wasShareLinks)
          payload.data.scenarioShareLinksEnabled = nextShareLinks
        try {
          const result = await institutionApi.patch('', payload)
          let error = null
          ;({ error, institution } = result.data.response)
          this.statusEditingInstitution = result.data.status
          if (error) this.errorEditingInstitution = error.message
          else {
            const updated = institution[0] || {}
            this.activeInstitutions = (this.activeInstitutions || []).map((row) =>
              row.id === id ? { ...row, ...updated } : row
            )
            this.setAdminTableData()
            await this.fetchActiveInstitutions(false)
          }
        } catch (e) {
          this.errorEditingInstitution =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
        }
        this.isLoadingEditingInstitution = false
      }
    },
    async creatingInstitution(institution) {
      let {
        name,
        country,
        state,
        url,
        hubspotUrl,
        directAccessEnabled,
        scenarioShareLinksEnabled
      } = institution
      if (name != null) name = name.replace(/^\s+|\s+$/g, '')
      if (url != null) url = url.replace(/^\s+|\s+$/g, '')
      if (hubspotUrl != null) hubspotUrl = hubspotUrl.replace(/^\s+|\s+$/g, '')
      this.errorCreatingInstitution = null
      this.isLoadingCreatingInstitution = true
      this.statusCreatingInstitution = null
      const payload = {
        data: {
          name,
          countryCode: country?.code || null,
          state: state?.code || null,
          url,
          hubspotUrl,
          directAccessEnabled: !!directAccessEnabled,
          scenarioShareLinksEnabled: !!scenarioShareLinksEnabled
        }
      }
      try {
        const result = await institutionApi.post('', payload)
        let error = null
        ;({ error, institution } = result.data.response)
        this.statusCreatingInstitution = result.data.status
        if (error) this.errorCreatingInstitution = error.message
        else this.activeInstitutions.unshift(institution)
      } catch (e) {
        this.errorCreatingInstitution =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingCreatingInstitution = false
    },
    setFetchActiveInstitutionsInterval(newValue) {
      if (newValue == null && this.fetchActiveInstitutionsInterval != null)
        clearInterval(this.fetchActiveInstitutionsInterval)
      this.fetchActiveInstitutionsInterval = newValue
    },
    setFetchArchivedInstitutionsInterval(newValue) {
      if (newValue == null && this.fetchArchivedInstitutionsInterval != null)
        clearInterval(this.fetchArchivedInstitutionsInterval)
      this.fetchArchivedInstitutionsInterval = newValue
    },
    setEditInstitution(newValue) {
      this.editInstitution = newValue
    },
    setAdminTableData() {
      const {
        data,
        headers,
        itemsPerPage,
        value,
        filterKeys,
        customFilters,
        customSort
      } = this.getAdminTabs?.[this.adminTabIndex] || {}
      this.adminTableData = {
        data,
        headers,
        itemsPerPage,
        value,
        filterKeys,
        customFilters,
        customSort
      }
    },
    setItemsPerPage({ itemsPerPage, value }) {
      this.itemsPerPage[value] = itemsPerPage
    },
    cancelError(key) {
      switch (key) {
        case 'activeInstitutions':
          this.errorActiveInstitutions = null
          break
        case 'archivedInstitutions':
          this.errorArchivedInstitutions = null
          break
        case 'archivingInstitutions':
          this.errorArchivingInstitutions = null
          break
        case 'restoringInstitutions':
          this.errorRestoringInstitutions = null
          break
        case 'editingInstitution':
          this.errorEditingInstitution = null
          break
        case 'creatingInstitution':
          this.errorCreatingInstitution = null
          break
        default:
          break
      }
    }
  }
})
